import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, formatApiError } from "@/lib/api";
import { useFormState } from "@/lib/hooks";

// Generic create/edit dialog: renders fields via render(form, set)
export const EntityDialog = ({ open, onOpenChange, resource, title, initial, defaults, onSaved, render, testid, confirmBeforeSave, afterSave }) => {
  const { form, setForm, set } = useFormState({ ...defaults, ...initial });
  const [saving, setSaving] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState(null);

  useEffect(() => {
    if (open) setForm({ ...defaults, ...initial });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    const msg = confirmBeforeSave?.(form, initial);
    if (msg && !confirmMsg) {
      setConfirmMsg(msg);
      return;
    }
    setConfirmMsg(null);
    setSaving(true);
    try {
      const { data } = form.id
        ? await api.patch(`/${resource}/${form.id}`, form)
        : await api.post(`/${resource}`, form);
      toast.success(form.id ? "Modifications enregistrées" : "Élément créé");
      afterSave?.(data, initial);
      onOpenChange(false);
      onSaved?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={testid} className="max-h-[90vh] overflow-y-auto bg-[#0F1F3D] border-cyan-400/20 sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{form.id ? `Modifier ${title}` : `Nouveau : ${title}`}</DialogTitle>
          <DialogDescription className="text-slate-400">Les champs marqués * sont obligatoires.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{render(form, set)}</div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid={`${testid}-cancel`}>
              Annuler
            </Button>
            <Button type="submit" disabled={saving} data-testid={`${testid}-submit`} className="dt-glow">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </div>
        </form>
        <AlertDialog open={!!confirmMsg} onOpenChange={(o) => !o && setConfirmMsg(null)}>
          <AlertDialogContent className="bg-[#0F1F3D] border-cyan-400/20">
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmer l'enregistrement</AlertDialogTitle>
              <AlertDialogDescription data-testid={`${testid}-confirm-message`}>{confirmMsg}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`${testid}-confirm-cancel`}>Annuler</AlertDialogCancel>
              <AlertDialogAction data-testid={`${testid}-confirm-ok`} onClick={submit}>Confirmer</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};
